import { Fragment } from 'react';
import TabsView from 'UI/components/templates/TabsView';

import { useStyles } from './ProfileTabs.styled';

/**
 * The tabs of a profile. Each build<Entity>Tabs declares its own `tabs`; this component only
 * filters by `visible`, keeps the active tab in the URL, keeps the tab order the user saved and
 * builds the key of each tab in one place from `getTabVersion`, so refreshTab can reload a single tab.
 * V1 tab sections stay as they are: `content` is the element of the section.
 * The saved order is the V1 one: TabsView stores it under `organizationStorageKey` and shows
 * `organizationSubheader` in its organize drawer (V1 ProfileTabsWrapper).
 * @param {Object} props
 * @param {Array<{
 *   id: string,
 *   label: string,
 *   content: React.ReactNode,
 *   visible: boolean
 * }>} props.tabs
 * @param {(tabId: string) => number} [props.getTabVersion] From useTabRefresh. Without it every
 *   tab stays at version 0
 * @param {boolean} [props.enableUrlNavigation] true in the route, false in the quick view
 * @param {string} props.organizationStorageKey `ENTITIES.<entity>.tabsStorageKey` (PERSISTENCE_KEYS.PROFILE_TABS.*)
 * @param {string} props.organizationSubheader `ENTITIES.<entity>.tabsSubheader` ("Candidate Profile", ...)
 */
export const ProfileTabs = ({
  enableUrlNavigation = false,
  getTabVersion = () => 0,
  organizationStorageKey,
  organizationSubheader,
  tabs
}) => {
  const classes = useStyles();
  const visibleTabs = tabs.filter(tab => tab.visible);

  const views = tabs.map(tab => ({
    id: tab.id,
    label: tab.label,
    visible: tab.visible,
    view: <Fragment key={`${tab.id}:${getTabVersion(tab.id)}`}>{tab.content}</Fragment>
  }));

  return (
    <TabsView
      className={classes.tabsRoot}
      enableUrlNavigation={enableUrlNavigation}
      initialTab={visibleTabs[0]?.id}
      organizationStorageKey={organizationStorageKey}
      organizationSubheader={organizationSubheader}
      padding={32}
      tabPanelProps={{ className: classes.overviewTab }}
      tabs={views}
      useIndexes={false}
    />
  );
};
